import { Alert, Box, Button, Flex, Text } from '@backstage/ui';

type ImportResult = {
  total: number;
  imported: string[];
  skipped: string[];
  failed: Array<{ id?: string; reason: string }>;
};

type ImportResultBannerProps = {
  result: ImportResult;
  itemLabel: string;
  onDismiss: () => void;
};

export const ImportResultBanner = ({
  result,
  itemLabel,
  onDismiss,
}: ImportResultBannerProps) => {
  const hasProblems = result.skipped.length > 0 || result.failed.length > 0;
  const allFailed = result.total > 0 && result.failed.length === result.total;
  let status: 'danger' | 'warning' | 'success' = 'success';
  let title = `${itemLabel} import complete`;
  if (allFailed) {
    status = 'danger';
    title = `${itemLabel} import failed`;
  } else if (hasProblems) {
    status = 'warning';
    title = `${itemLabel} import completed with errors`;
  }
  const summary = `${result.total} total: ${result.imported.length} imported, ${result.skipped.length} skipped, ${result.failed.length} failed.`;

  return (
    <Alert
      style={{ alignItems: 'flex-start' }}
      status={status}
      icon
      title={title}
      description={
        <Flex direction="column" gap="3">
          <Text>{summary}</Text>
          {result.skipped.length > 0 ? (
            <Text>Skipped existing IDs: {result.skipped.join(', ')}</Text>
          ) : null}
          {result.failed.map((failure, index) => {
            const failureKey = `${failure.id ?? 'unknown'}-${index}`;
            return (
              <Flex key={failureKey} direction="column" gap="3">
                {index > 0 ? (
                  <hr
                    style={{
                      border: 0,
                      borderTop: '1px solid currentColor',
                      margin: 0,
                      opacity: 0.2,
                      width: '100%',
                    }}
                  />
                ) : null}
                <Flex direction="column" gap="1">
                  <Text variant="body-small" color="secondary">
                    {itemLabel}
                  </Text>
                  <Text weight="bold">{failure.id ?? `Item ${index + 1}`}</Text>
                </Flex>
                <Flex direction="column" gap="1">
                  <Text variant="body-small" weight="bold">
                    Issues
                  </Text>
                  <Box
                    as="ul"
                    style={{ margin: 0, paddingInlineStart: '1.5rem' }}
                  >
                    {failure.reason.split('; ').map((reason, reasonIndex) => (
                      <li
                        key={`${failureKey}-${reasonIndex}`}
                        style={{ paddingBlock: '0.125rem' }}
                      >
                        <Text variant="body-small">{reason}</Text>
                      </li>
                    ))}
                  </Box>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      }
      customActions={
        <Button variant="tertiary" onClick={onDismiss}>
          Dismiss
        </Button>
      }
    />
  );
};
